import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { DialogTitle, DialogContent, DialogActions, Dialog, Button } from '@mui/material';
import useAppLanguage from '@/hooks/useAppLanguage';
import { useTranslation } from 'react-i18next';
import { getUserPreferences } from '@/redux/User/user.reducer';
import { setUserPreference } from '@/redux/User/user.action';
import { ExecutiveSummaryPreferences, ReduxForms, SummaryUserPreferences } from '@/types';
import InputTextSummary from './SummaryInput';
import styles from './DialogSummary.module.scss';

interface DialogSummaryProps {
  close: () => void;
  confirm: (preferences: SummaryUserPreferences) => void;
  formsValues: ReduxForms;
  userPreferences: SummaryUserPreferences;
  setPreferences: (preferences: SummaryUserPreferences) => void;
}

const DialogSummary: React.FC<DialogSummaryProps> = (props) => {
  const { close, confirm, formsValues, userPreferences, setPreferences } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [selectionList, setSelectionList] = useState<ExecutiveSummaryPreferences[]>([]);
  const checkLabel = (form) => Object.entries(formsValues.options).find((option) => option[0] === form)?.[1]?.[lang] ?? t('notAvailable');

  const deleteList = (fieldLabel: ExecutiveSummaryPreferences) => {
    setSelectionList(Object.values(selectionList).filter((forms) => fieldLabel !== forms));
  };

  const addList = (fieldLabel: ExecutiveSummaryPreferences) => {
    const newPreference = Object.values(selectionList);

    if (newPreference.length < 4 && newPreference.filter((preference) => preference === fieldLabel).length < 1) {
      newPreference.push(fieldLabel);
      setSelectionList(newPreference);
    }
  };

  const handleConfirm = () => {
    setPreferences({ executiveSummaryPreferences: selectionList });
    confirm({ executiveSummaryPreferences: selectionList });
  };

  useEffect(() => {
    setSelectionList(userPreferences.executiveSummaryPreferences);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (

    <Dialog
      open
      maxWidth="md"
      fullWidth
      onClose={close}
      aria-labelledby="form-dialog-dialog"
    >
      <DialogTitle id="form-dialog-summary-title">{t('summary.popupLabel')}</DialogTitle>
      <DialogContent>
        <div className={styles.Summary}>
          <div className={styles.RowContainer}>
            {formsValues?.options !== undefined && (
              <div className={styles.AvailableValues}>
                <h2> {t('summary.avalaibleValue')} </h2>
                <div className={styles.overflow}>
                  {Object.entries(formsValues?.options).map((forms) => (
                    <InputTextSummary
                      id={`summary-userspreference-available-${forms[0]}`}
                      key={forms[0]}
                      label={forms[1][lang]}
                      value={forms[0] as ExecutiveSummaryPreferences}
                      add
                      addList={addList}
                    />
                  ))}
                </div>
              </div>
            )}
            {selectionList && (
              <div className={styles.ShownValues}>
                <h2> {t('summary.shownValues')} </h2>
                {Object.values(selectionList).map((form) => (
                  <InputTextSummary
                    id={`summary-userspreference-selection-${form}`}
                    key={form}
                    label={checkLabel(form)}
                    value={form}
                    clearList
                    deleteList={deleteList}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={handleConfirm}>{t('dialog.confirm')}</Button>
        <Button color="primary" onClick={close}>{t('dialog.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  userPreferences: getUserPreferences(state),
});

const actionCreators = {
  setPreferences: setUserPreference,
};

export default (connect(mapStateToProps, actionCreators)(DialogSummary));
